import { reactive } from "vue";

/**
 * Generic, config-agnostic resolver for which region ("primary" or "failover")
 * is currently active per environment. Deliberately has no dependency on
 * aws-configuration.ts to avoid a circular import — callers pass the status
 * URL to check in.
 */
export type ActiveRegion = "primary" | "failover";

// Wrapped in Vue's reactive() (not a plain Map) so that any computed/watchEffect
// reading getActiveRegion(key) - e.g. initClientReactive's baseUrl computed -
// automatically re-runs when the region changes, with no extra wiring needed.
const resolvedRegion = reactive(new Map<string, ActiveRegion>());

/** Synchronous read of the last-known active region for a key. Defaults to "primary". */
export function getActiveRegion(key: string): ActiveRegion {
	return resolvedRegion.get(key) ?? "primary";
}

/**
 * Fetches statusUrl and updates the resolved region for `key` if it parses
 * successfully. On any failure (empty URL, network error, bad JSON), leaves
 * the currently-resolved value untouched — a transient blip should not force
 * a region back to primary if failover is genuinely active, and should not
 * force a switch to failover on a one-off hiccup either.
 */
export async function checkActiveRegionOnce(
	key: string,
	statusUrl: string,
): Promise<ActiveRegion> {
	if (!statusUrl) return getActiveRegion(key);

	try {
		const response = await fetch(statusUrl, { cache: "no-store" });
		if (!response.ok) return getActiveRegion(key);

		const data = (await response.json()) as { activeRegion?: string };
		if (data.activeRegion === "primary" || data.activeRegion === "failover") {
			resolvedRegion.set(key, data.activeRegion);
		}
	} catch (error) {
		console.warn(`[active-region] Failed to check ${statusUrl}:`, error);
	}

	return getActiveRegion(key);
}
